import Jsonld from "@/components/Jsonld";
import { Star, Quote, MapPin } from "lucide-react";
import { REVIEWS, aggregateRating, reviewLd } from "@/data/reviews";

/**
 * Renders a testimonials section + injects Review JSON-LD (aggregate + per-review).
 * - `reviews` = subset to display (defaults to first 6 from REVIEWS)
 * - `itemName` = the Service name passed to JSON-LD itemReviewed
 * - `id` = JSON-LD script id (must be unique per page)
 */
export default function Testimonials({ reviews = REVIEWS.slice(0, 6), itemName = "Appliance Repair", id = "ld-reviews" }) {
  const agg = aggregateRating();
  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://spaceappliancerepair.com/#business",
    name: "Space Appliance Repair",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: agg.ratingValue,
      reviewCount: agg.reviewCount,
      bestRating: 5,
    },
    review: reviewLd(reviews, itemName),
  };

  return (
    <section id="reviews" className="relative z-10 py-20 sm:py-24 border-y border-cream/10 bg-navy-2/40">
      <Jsonld id={id} data={data} />
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <div className="chip"><Star className="w-3.5 h-3.5" /> 5.0 ★ · 111 five-star Google reviews</div>
          <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">What San Antonio says</h2>
          <p className="text-cream/60 mt-3 max-w-xl mx-auto">Real customers, real jobs. Names initialed for privacy.</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reviews.map((r) => (
            <article key={r.id} data-testid={`review-${r.id}`} className="bg-navy/60 border border-cream/15 rounded-lg p-6 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange/15 text-orange grid place-items-center display-retro text-sm border border-orange/40">
                    {r.initials}
                  </div>
                  <div>
                    <div className="display-retro text-cream text-sm">{r.author}</div>
                    <div className="text-[10px] uppercase tracking-widest text-cream/50 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-2.5 h-2.5" /> {r.neighborhood}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-gold fill-current" />
                  ))}
                </div>
              </div>
              <Quote className="w-4 h-4 text-orange/60 mb-2" />
              <p className="text-cream/80 text-sm leading-relaxed flex-1">{r.body}</p>
              <div className="mt-4 pt-3 border-t border-cream/10 text-[10px] uppercase tracking-widest text-cream/50 flex items-center justify-between">
                <span>{r.brand} · {r.appliance}</span>
                <span>{new Date(r.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
